import { fetchProcesses } from "../../services/state/processes/processes-async-calls";
import { fetchCustomDataTypesByApplication } from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { AppDispatch, RootState } from "@/services/state/store";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchRules } from "../../services/state/rules/rules-async-calls";
import { HStack, Icon, Text, useDisclosure } from "@chakra-ui/react";
import { RiFlowChart } from "react-icons/ri";
import { FaCode, FaRegFolder } from "react-icons/fa6";
import { Option } from "../../components/Select/DropDown";
import { VscTypeHierarchy } from "react-icons/vsc";
import { fetchFoldersByApplication } from "../../services/state/folders/folders-async-calls";
import { fetchConnectedSystemsByApplication } from "../../services/state/connected-systems/connectedSystem-async-calls";
import { GrSystem } from "react-icons/gr";
import { fetchHttpIntegrationsByApplication } from "../../services/state/http-integration/httpIntegration-async-calls";
import { AiOutlineApi, AiTwotoneApi } from "react-icons/ai";
import { fetchRecordTypesByApplication } from "../../services/state/record-types/recordTypes-async-calls";
import { PiDatabase } from "react-icons/pi";
import { fetchApplicationsByUuid } from "../../services/state/applications/applications-async-calls";
import { fetchWebApiByAppUuid } from "../../services/state/web-api/web-api-async-calls";
import { formatTableDate } from "../../utils/dateFormatter";

export interface Objects {
  uuid: string;
  name: string;
  description: string;
  type:
    | "cdt"
    | "rule"
    | "process"
    | "folder"
    | "cs"
    | "intg"
    | "recordType"
    | "webApi";
  lastModified: Date;
}

export const icons = {
  process: <Icon as={RiFlowChart as React.ElementType} color="green" />,
  rule: <Icon as={FaCode as React.ElementType} color="orange" />,
  cdt: <Icon as={VscTypeHierarchy as React.ElementType} color="cyan" />,
  cs: <Icon as={GrSystem as React.ElementType} color="coral" />,
  intg: <Icon as={AiTwotoneApi as React.ElementType} color="#FF00FF" />,
  folder: <Icon as={FaRegFolder as React.ElementType} color="teal" />,
  recordType: <Icon as={PiDatabase as React.ElementType} color="green" />,
  webApi: <Icon as={AiOutlineApi as React.ElementType} color="orange" />,
};

export const useObjects = (filters: {
  search: string;
  checkboxes: Record<string, boolean>;
  timeRange: string;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const filterObjects = useCallback(
    (data: Objects[]) => {
      const now = new Date();
      return data.filter((item) => {
        // Search filter
        const searchMatch =
          item.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          item.description.toLowerCase().includes(filters.search.toLowerCase());

        // Type filter
        const typeMap: Record<string, keyof typeof filters.checkboxes> = {
          rule: "rule",
          process: "process",
          cdt: "customDataType",
          cs: "connectedSystems",
          intg: "integration",
          folder: "floder",
          recordType: "recordType",
          webApi: "webApi",
        };

        const typeChecked =
          !Object.values(filters.checkboxes).some(Boolean) ||
          filters.checkboxes[typeMap[item.type]];

        // Time filter
        let timeMatch = true;
        const itemDate = item.lastModified;
        switch (filters.timeRange) {
          case "time-24h":
            timeMatch =
              itemDate >= new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case "time-7d":
            timeMatch =
              itemDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
        }

        return searchMatch && typeChecked && timeMatch;
      });
    },
    [filters]
  );

  const { applicationId } = useParams();

  const {
    isOpen: isAddCustomDataTypeModalOpen,
    onOpen: onAddCustomDataTypeModalOpen,
    onClose: onAddCustomDataTypeModalModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddConnectedSystemModalOpen,
    onOpen: onAddConnectedSystemModalOpen,
    onClose: onAddConnectedSystemModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddHttpIntegrationModalOpen,
    onOpen: onAddHttpIntegrationModalOpen,
    onClose: onAddHttpIntegrationModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddProcessModalOpen,
    onOpen: onAddProcessModalOpen,
    onClose: onAddProcessModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddRuleModalOpen,
    onOpen: onAddRuleModalOpen,
    onClose: onAddRuleModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddFolderModalOpen,
    onOpen: onAddFolderModalOpen,
    onClose: onAddFolderModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddRecordTypeModalOpen,
    onOpen: onAddRecordTypeModalOpen,
    onClose: onAddRecordTypeModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddWebApiModalOpen,
    onOpen: onAddWebApiModalOpen,
    onClose: onAddWebApiModalClose,
  } = useDisclosure();

  useEffect(() => {
    dispatch(fetchApplicationsByUuid(applicationId as string));
    dispatch(fetchCustomDataTypesByApplication(applicationId as string));
    dispatch(fetchConnectedSystemsByApplication(applicationId as string));
    dispatch(fetchHttpIntegrationsByApplication(applicationId as string));
    dispatch(fetchProcesses(applicationId as string));
    dispatch(fetchRules(applicationId as string));
    dispatch(fetchFoldersByApplication(applicationId as string));
    dispatch(fetchRecordTypesByApplication(applicationId as string));
    dispatch(fetchWebApiByAppUuid(applicationId as string));
  }, []);

  const customDataTypes: Objects[] = useSelector(
    (state: RootState) => state.customDataTypes.customDataTypes
  ).map((cdt) => ({
    uuid: cdt.uuid,
    name: cdt.dataTypeName,
    description: "",
    type: "cdt",
    lastModified: new Date(cdt.updatedOn),
  }));

  const connectedSystems: Objects[] = useSelector(
    (state: RootState) => state.connectedSystems.connectedSystems
  ).map((cs) => ({
    uuid: cs.uuid,
    name: cs.csNm,
    description: cs.csDesc,
    type: "cs",
    lastModified: new Date(cs.updatedOn),
  }));

  const httpIntegrations: Objects[] = useSelector(
    (state: RootState) => state.httpIntegrations.httpIntegrations
  ).map((intg) => ({
    uuid: intg.uuid,
    name: intg.intgNm,
    description: intg.intgDesc,
    type: "intg",
    lastModified: new Date(intg.updatedOn),
  }));

  const processes: Objects[] = useSelector(
    (state: RootState) => state.processes.processes
  ).map((process) => ({
    uuid: process.uuid,
    name: process.processNm,
    description: process.processDesc,
    type: "process",
    lastModified: new Date(process.updatedOn),
  }));

  const rules: Objects[] = useSelector(
    (state: RootState) => state.rules.rules
  ).map((rule) => ({
    uuid: rule.uuid,
    name: rule.ruleNm,
    description: "",
    type: "rule",
    lastModified: new Date(rule.updatedOn),
  }));

  const folders: Objects[] = useSelector(
    (state: RootState) => state.folders.folders
  ).map((folder) => ({
    uuid: folder.folderUuid,
    name: folder.folderNm,
    description: "",
    type: "folder",
    lastModified: new Date(folder.updatedOn),
  }));

  const recordTypes: Objects[] = useSelector(
    (state: RootState) => state.recordTypes.recordTypes
  ).map((type) => ({
    uuid: type.uuid,
    name: type.recordName,
    description: "",
    type: "recordType",
    lastModified: new Date(type.updatedOn),
  }));

  const webApi: Objects[] = useSelector(
    (state: RootState) => state.webApi.webApis
  ).map((api) => ({
    uuid: api.uuid,
    name: api.apiNm,
    description: "",
    type: "webApi",
    lastModified: new Date(api.updatedOn),
  }));

  const createNewOptions = [
    { id: "rule", name: "rule", component: <Text>Rule</Text> },
    { id: "process", name: "process", component: <Text>Process</Text> },
    {
      id: "custom data type",
      name: "custom data type",
      component: <Text>Custom data type</Text>,
    },
    {
      id: "connected system",
      name: "connected system",
      component: <Text>Connected system</Text>,
    },
    {
      id: "integration",
      name: "integration",
      component: <Text>Integration</Text>,
    },
    {
      id: "folder",
      name: "folder",
      component: <Text>Folder</Text>,
    },
    {
      id: "recordType",
      name: "record type",
      component: <Text>Record Type</Text>,
    },
    {
      id: "webApi",
      name: "web API",
      component: <Text>Web API</Text>,
    },
  ];

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Description",
      accessor: "description",
    },
    {
      Header: "Last modified",
      accessor: "lastModified",
    },
    {
      Header: "",
      accessor: "actions",
    },
  ];

  const onObjectClick = (
    type:
      | "process"
      | "rule"
      | "cdt"
      | "folder"
      | "cs"
      | "intg"
      | "recordType"
      | "webApi",
    uuid: string
  ) => {
    switch (type) {
      case "process":
        window.open(
          `/platform/design/process/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;

      case "rule":
        window.open(
          `/platform/design/rule/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;

      case "cdt":
        window.open(
          `/platform/design/custom-data-type/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "cs":
        window.open(
          `/platform/design/connected-system/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "intg":
        window.open(
          `/platform/design/http-integration/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "folder":
        window.open(
          `/platform/design/folder/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "recordType":
        window.open(
          `/platform/design/record-type/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "webApi":
        window.open(
          `/platform/design/web-api/${uuid}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      default:
        break;
    }
  };

  const columnData = useMemo(() => {
    const rawData = [
      ...processes,
      ...customDataTypes,
      ...connectedSystems,
      ...httpIntegrations,
      ...rules,
      ...folders,
      ...recordTypes,
      ...webApi,
    ];

    return filterObjects(rawData)
      .sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())
      .map((data) => ({
        name: (
          <HStack
            onClick={() => onObjectClick(data.type, data.uuid)}
            cursor="pointer"
            gap={2}
          >
            {icons[data.type]}
            <Text
              color="brand.500"
              cursor="pointer"
              _hover={{
                color: "brand.700",
                // textDecoration: "underline",
              }}
              transition="all 0.2s ease"
              fontWeight="semibold"
              textUnderlineOffset="2px"
            >
              {data.name}
            </Text>
          </HStack>
        ),
        description: data.description,
        type: data.type,
        lastModified: (
          <Text fontSize="sm" color="rgba(255, 255, 255, 0.8)" fontWeight="500" whiteSpace="nowrap" >
            {formatTableDate(data.lastModified)}
          </Text>
        ),
      }));
  }, [
    // Include all dependencies from Redux state
    processes,
    customDataTypes,
    connectedSystems,
    httpIntegrations,
    rules,
    folders,
    recordTypes,
    filterObjects,
  ]);

  const onOptionChange = (selectedOptions: Option | Option[]) => {
    if (!Array.isArray(selectedOptions)) {
      switch (selectedOptions.id) {
        case "rule":
          onAddRuleModalOpen();
          break;
        case "process":
          onAddProcessModalOpen();
          break;
        case "custom data type":
          onAddCustomDataTypeModalOpen();
          break;
        case "connected system":
          onAddConnectedSystemModalOpen();
          break;
        case "integration":
          onAddHttpIntegrationModalOpen();
          break;
        case "folder":
          onAddFolderModalOpen();
          break;
        case "recordType":
          onAddRecordTypeModalOpen();
          break;
        case "webApi":
          onAddWebApiModalOpen();
          break;
        default:
          break;
      }
    }
  };

  return {
    createNewOptions,
    columns,
    columnData,
    onOptionChange,
    isAddRuleModalOpen,
    onAddRuleModalClose,
    isAddCustomDataTypeModalOpen,
    onAddCustomDataTypeModalModalClose,
    isAddConnectedSystemModalOpen,
    onAddConnectedSystemModalClose,
    isAddHttpIntegrationModalOpen,
    onAddHttpIntegrationModalClose,
    isAddProcessModalOpen,
    onAddProcessModalClose,
    isAddFolderModalOpen,
    onAddFolderModalClose,
    isAddRecordTypeModalOpen,
    onAddRecordTypeModalClose,
    isAddWebApiModalOpen,
    onAddWebApiModalClose,
  };
};
